// pages/MarketLanding.jsx
// Public DWI Market page — dwifintech.com/dwi-market, and one page per product
// at /dwi-market/p/<slug> (shareable; link previews come from the server,
// services/marketSharePage.js).
//
// Shows DWI's live products (photos, sizes, prices or "price on request") with
// DWI as the only seller — supplier, cost and margin never reach this page
// (server whitelist: marketService.forPublic). Sends people to sign-up (with
// ?next= so they land in the shop) and to DWI's WhatsApp + phone from
// Admin → Market → Settings. Follows two switches:
//   - the page's own switch (dwi_market_landing): OFF = admins see a preview,
//     everyone else goes to the login page;
//   - the shop switch (dwi_market): closed = "opening soon, sign up now",
//     selling via WhatsApp; open = "order now", selling via in-app message.
// Visits and clicks are counted anonymously per traffic source (?src= or
// ?utm_source= on the ad link) — no referral codes.
// All wording lives in COPY below.
import { useState, useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { marketAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { DWILogoIcon } from '../components/DWILogo';
import { Spinner } from '../components/ui';

const COPY = {
  pageTitle: 'DwiSend — Buy. Sell. Send. Delivered.',
  tagline: 'Buy. Sell. Send. Delivered.',
  heroTitle: 'Buy from a name you know: DWI.',
  heroSub: 'Food and staples, farm produce, fish, electricals and more. You order from DWI and pay DWI — safely, with your DWIPay Wallet or Mobile Money.',
  closedBanner: 'Opening soon — create your free account now so you are ready on day one.',
  openBanner: 'Now open — order today.',
  buyClosed: 'Sign up to buy',
  buyOpen: 'Shop now',
  sell: 'Sell to DWI',
  whatsapp: 'WhatsApp us',
  login: 'Already have an account? Log in',

  productsTitle: 'What DWI is selling',
  productsSub: 'Prices are set by DWI and shown up front. The delivery fee for your area is shown at checkout.',
  search: 'Search products…',
  all: 'All',
  noProducts: 'New products are being added. Tell us what you need on WhatsApp.',
  noMatch: 'Nothing matches your search.',
  priceOnRequest: 'Price on request',
  outOfStock: 'out of stock',
  minOrder: n => `min. ${n}`,
  order: 'Order now',
  orderClosed: 'Sign up to order',
  orderSoon: 'Ordering opens soon',
  askPrice: 'Ask DWI for a price',
  askAbout: 'WhatsApp DWI about this',
  share: 'Share',
  linkCopied: 'Link copied — paste it anywhere',
  backToAll: '← All products',
  notFound: 'This product is no longer listed. Here is what DWI is selling now.',
  soldBy: 'Sold by DWI',
  fresh: 'Fresh / perishable',
  conditions: { new: 'New', used: 'Used', refurbished: 'Refurbished' },

  cantFindTitle: 'Can’t find it? Tell us what you need.',
  cantFindSub: 'Send DWI the product, the quantity and where you are. We will find it and give you a price.',
  cantFindCta: 'Tell DWI on WhatsApp',
  cantFindRequestCta: 'Ask DWI for a price',

  buyersTitle: 'Buying from DwiSend',
  buyerSteps: [
    ['Choose', 'Pick a product and the size you need — by the piece, kilo, bag, litre or tonne. Prices are shown up front.'],
    ['Pay', 'Pay with your DWIPay Wallet or Mobile Money. Your order is confirmed once payment is received.'],
    ['Receive', 'We deliver to the areas we serve and tell you who is bringing your order. The delivery fee is shown before you pay.'],
  ],
  buyerBenefits: [
    ['One seller you can hold to account', 'You buy from DWI — not from a stranger online.'],
    ['Clear prices for every size', 'The price you see is the price you pay, plus the delivery fee shown at checkout.'],
    ['Buying in bulk?', 'Schools, restaurants, shops and contractors can order by the bag, the tonne or the carton.'],
    ['Track every order', 'See each order in your account, from payment to delivery.'],
  ],

  sellersTitle: 'Have goods to sell? Sell to DWI.',
  sellersSub: 'Farmers, fishers, traders and suppliers: DWI buys for DwiSend. You deal with DWI only — we handle the buyers and delivery.',
  sellerSteps: [
    ['Reach us', 'Message us on WhatsApp, or create a free DWI account and send us a message in the app.'],
    ['Tell us what you have', 'Product, quantity, your price, where it is, and when it is ready.'],
    ['Agree the deal', 'If it is a fit, we agree price, quantity, quality and pickup with you — and how you are paid — before anything moves.'],
  ],
  sellersClosedNote: 'Until the shop opens, please reach us on WhatsApp.',

  faqTitle: 'Questions',
  faq: [
    ['Who am I buying from?', 'From DWI. Everything on DwiSend is sold by DWI FINTECH (U) LIMITED, and you pay DWI.'],
    ['How do I pay?', 'With your DWIPay Wallet or Mobile Money, on a secure DWI payment page.'],
    ['Where do you deliver?', 'The areas we deliver to are listed at checkout, each with its fee. Not in your area yet? WhatsApp us.'],
    ['How do I sell to DWI?', 'WhatsApp us, or sign up and send us a message in the app with what you have. We will discuss it with you.'],
    ['Does it cost anything to sign up?', 'No — creating a DWI account is free.'],
  ],
  contactTitle: 'Talk to DwiSend',
  contactSub: 'Questions about buying or selling? Reach us directly.',
  waBuyerText: 'Hello DwiSend, I saw your page and I would like to know more.',
  waSellerText: 'Hello DwiSend, I would like to sell to DWI. I have: ',
  waNeedText: 'Hello DwiSend, I am looking for: ',
  waProductText: (name, link) => `Hello DwiSend, I am interested in ${name}. ${link}`,
  shareText: name => `${name} — from DwiSend`,
  footer: '© DWI FINTECH (U) LIMITED',
};

const SELL_IN_APP = '/market?tab=messages&topic=selling';
const SELL_SIGNUP = '/market?tab=sell';   // Phase 2 seller sign-up (own switch)
const BUY_REQUEST = '/market?tab=requests';   // Phase 3 buy requests (own switch)
const ugx = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;

function source() {
  const p = new URLSearchParams(window.location.search);
  return p.get('src') || p.get('utm_source') || '';
}

// Fire-and-forget; survives the page navigating away. The server ignores it while the page is OFF.
function track(event) {
  try {
    const body = JSON.stringify({ event, src: source() });
    const url = '/api/market/landing/event';
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch (_) { /* counting must never break the page */ }
}

const productLink = p => `${window.location.origin}/dwisend/p/${p.slug}`;

async function shareProduct(p) {
  track('click_share');
  const url = `${productLink(p)}?src=share`;
  const text = COPY.shareText(p.name);
  if (navigator.share) {
    try { await navigator.share({ title: text, text, url }); return; } catch (_) { /* closed or unsupported → copy */ }
  }
  try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success(COPY.linkCopied); }
  catch (_) { window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank', 'noopener'); }
}

export default function MarketLanding() {
  const { slug } = useParams();
  const { user, isAuthenticated, loading } = useAuth();
  const [state, setState] = useState(null);       // { enabled, shopOpen, contact }
  const [failed, setFailed] = useState(false);
  const [catalog, setCatalog] = useState(null);   // { products, categories }
  const [product, setProduct] = useState(null);   // the /p/<slug> product, or false = not found
  const isAdmin = user?.role === 'admin';
  const canSee = state && (state.enabled || isAdmin);

  useEffect(() => {
    marketAPI.getLanding().then(setState).catch(() => setFailed(true));
    const before = document.title;
    document.title = COPY.pageTitle;
    return () => { document.title = before; };
  }, []);

  // Products: the public route while the page is ON, the admin preview while OFF.
  useEffect(() => {
    if (!canSee || loading) return;
    const preview = !state.enabled;
    (preview ? marketAPI.previewCatalog() : marketAPI.publicCatalog())
      .then(r => setCatalog({ products: r.products || [], categories: r.categories || [] }))
      .catch(err => { setCatalog({ products: [], categories: [] }); toast.error(err.message); });
  }, [canSee, loading, state?.enabled]);

  useEffect(() => {
    if (!slug || !canSee || loading) { setProduct(null); return; }
    (state.enabled ? marketAPI.publicProduct(slug) : marketAPI.previewProduct(slug))
      .then(r => setProduct(r.product)).catch(() => setProduct(false));
    window.scrollTo(0, 0);
  }, [slug, canSee, loading, state?.enabled]);

  useEffect(() => {
    if (product) document.title = `${product.name} — DwiSend`;
    return () => { document.title = COPY.pageTitle; };
  }, [product]);

  // One visit per browser session; one product view per product page.
  useEffect(() => {
    if (!state?.enabled) return;
    try {
      if (sessionStorage.getItem('dwiMarketVisit')) return;
      sessionStorage.setItem('dwiMarketVisit', '1');
    } catch (_) { /* private mode: count anyway */ }
    track('visit');
  }, [state?.enabled]);
  useEffect(() => { if (state?.enabled && product) track('product_view'); }, [state?.enabled, product]);

  if (failed) return <Navigate to="/login" replace />;
  if (!state || loading) return <div className="min-h-screen bg-bg flex items-center justify-center"><Spinner size="xl" /></div>;
  if (!state.enabled && !isAdmin) return <Navigate to="/login" replace />;

  const { shopOpen, sellersOpen, requestsOpen, contact } = state;
  const wa = text => contact.whatsapp && `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(text)}`;
  const buyHref = isAuthenticated ? '/market' : '/register?next=/market';
  const inApp = target => (isAuthenticated ? target : `/register?next=${encodeURIComponent(target)}`);
  const sellHref = sellersOpen ? inApp(SELL_SIGNUP)
    : !shopOpen && contact.whatsapp ? wa(COPY.waSellerText)
    : inApp(SELL_IN_APP);

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      {!state.enabled && (
        <div className="bg-amber text-bg text-center text-xs font-bold px-4 py-2">
          PREVIEW — this page is OFF. Only admins can see it. Turn it on in Admin → DwiSend → Settings.
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <header className="flex items-center justify-between py-5">
          <a href="/dwisend" className="flex items-center gap-2">
            <DWILogoIcon size={32} />
            <span className="leading-tight">
              <span className="block font-extrabold text-lg">Dwi<span className="text-cyan">Send</span></span>
              <span className="block text-[11px] font-semibold text-text-muted">{COPY.tagline}</span>
            </span>
          </a>
          {!isAuthenticated && (
            <a href="/login?next=/market" onClick={() => track('click_login')} className="text-xs font-semibold text-text-muted hover:text-cyan">Log in</a>
          )}
        </header>

        {slug && product && (
          <ProductDetail p={product} shopOpen={shopOpen} isAuthenticated={isAuthenticated} buyHref={buyHref} wa={wa} />
        )}
        {slug && product === false && (
          <p className="rounded-xl bg-bg-panel border border-border p-4 text-sm text-text-secondary">{COPY.notFound}</p>
        )}

        {!slug && (
          <section className="py-8 sm:py-10">
            <div className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold mb-4 ${shopOpen ? 'bg-green/15 text-green' : 'bg-amber/15 text-amber'}`}>
              {shopOpen ? COPY.openBanner : COPY.closedBanner}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">{COPY.heroTitle}</h1>
            <p className="mt-4 text-text-secondary text-base leading-relaxed max-w-2xl">{COPY.heroSub}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Cta href={buyHref} event="click_buy">{shopOpen ? COPY.buyOpen : COPY.buyClosed}</Cta>
              <Cta href={sellHref} event="click_sell" kind="sell">{COPY.sell}</Cta>
              {contact.whatsapp && <Cta href={wa(COPY.waBuyerText)} event="click_whatsapp" kind="wa">{COPY.whatsapp}</Cta>}
            </div>
            {!isAuthenticated && (
              <a href="/login?next=/market" onClick={() => track('click_login')} className="inline-block mt-4 text-xs text-text-muted hover:text-cyan">{COPY.login}</a>
            )}
          </section>
        )}

        <Products catalog={catalog} currentSlug={slug} shopOpen={shopOpen} isAuthenticated={isAuthenticated} buyHref={buyHref} wa={wa} />

        {contact.whatsapp && (
          <section className="py-8 border-t border-border">
            <div className="rounded-2xl bg-bg-panel border border-border p-5 sm:flex sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-extrabold">{COPY.cantFindTitle}</h2>
                <p className="mt-1 text-sm text-text-secondary">{COPY.cantFindSub}</p>
              </div>
              <div className="mt-4 sm:mt-0 shrink-0">{requestsOpen
                ? <Cta href={inApp(BUY_REQUEST)} event="click_buy" kind="primary">{COPY.cantFindRequestCta}</Cta>
                : <Cta href={wa(COPY.waNeedText)} event="click_whatsapp" kind="wa">{COPY.cantFindCta}</Cta>}</div>
            </div>
          </section>
        )}

        <section className="py-8 border-t border-border">
          <h2 className="text-xl font-extrabold">{COPY.buyersTitle}</h2>
          <Steps steps={COPY.buyerSteps} color="text-cyan" />
          <ul className="mt-5 grid sm:grid-cols-2 gap-3">
            {COPY.buyerBenefits.map(([t, d]) => (
              <li key={t} className="flex gap-3">
                <span className="text-cyan font-bold">✓</span>
                <div><div className="font-bold text-sm">{t}</div><p className="text-sm text-text-secondary">{d}</p></div>
              </li>
            ))}
          </ul>
          <div className="mt-6"><Cta href={buyHref} event="click_buy">{shopOpen ? COPY.buyOpen : COPY.buyClosed}</Cta></div>
        </section>

        <section className="py-8 border-t border-border">
          <h2 className="text-xl font-extrabold">{COPY.sellersTitle}</h2>
          <p className="mt-2 text-text-secondary leading-relaxed">{COPY.sellersSub}</p>
          <Steps steps={COPY.sellerSteps} color="text-green" />
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Cta href={sellHref} event="click_sell" kind={sellHref.startsWith('http') ? 'wa' : 'sell'}>{COPY.sell}</Cta>
            {!shopOpen && !sellersOpen && <span className="text-xs text-text-muted">{COPY.sellersClosedNote}</span>}
          </div>
        </section>

        <section className="py-8 border-t border-border">
          <h2 className="text-xl font-extrabold">{COPY.faqTitle}</h2>
          <div className="mt-4 space-y-2">
            {COPY.faq.map(([q, a]) => (
              <details key={q} className="rounded-xl bg-bg-panel border border-border px-4 py-3">
                <summary className="cursor-pointer font-bold text-sm">{q}</summary>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {(contact.whatsapp || contact.phone) && (
          <section className="py-8 border-t border-border">
            <h2 className="text-xl font-extrabold">{COPY.contactTitle}</h2>
            <p className="mt-2 text-text-secondary">{COPY.contactSub}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {contact.whatsapp && <Cta href={wa(COPY.waBuyerText)} event="click_whatsapp" kind="wa">{COPY.whatsapp}</Cta>}
              {contact.phone && <Cta href={`tel:${contact.phone.replace(/\s/g, '')}`} event="click_call" kind="sell">📞 {contact.phone}</Cta>}
            </div>
          </section>
        )}

        <footer className="py-8 border-t border-border text-center text-[11px] text-text-muted">{COPY.footer}</footer>
      </div>
    </div>
  );
}

function Cta({ href, event, children, kind = 'primary' }) {
  const external = href.startsWith('http');
  return (
    <a href={href} onClick={() => track(event)}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-all ${{
        primary: 'bg-cyan text-bg hover:opacity-90',
        sell: 'bg-bg-panel text-text-primary border border-border hover:border-cyan',
        wa: 'bg-green text-bg hover:opacity-90',
      }[kind]}`}>
      {children}
    </a>
  );
}

function Steps({ steps, color }) {
  return (
    <ol className="mt-5 grid sm:grid-cols-3 gap-3">
      {steps.map(([t, d], i) => (
        <li key={t} className="rounded-xl bg-bg-panel border border-border p-4">
          <div className={`${color} text-xs font-extrabold`}>STEP {i + 1}</div>
          <div className="mt-1 font-bold">{t}</div>
          <p className="mt-1 text-sm text-text-secondary leading-relaxed">{d}</p>
        </li>
      ))}
    </ol>
  );
}

// ── Products ─────────────────────────────────────────────────────────────────
function Products({ catalog, currentSlug, ...actions }) {
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (catalog?.products || []).filter(p => p.slug !== currentSlug)
      .filter(p => !cat || String(p.categoryId) === cat)
      .filter(p => {
        const hay = [p.name, p.description, p.categoryName, p.details?.brand, p.details?.origin].join(' ').toLowerCase();
        return words.every(w => hay.includes(w));
      });
  }, [catalog, cat, q, currentSlug]);

  return (
    <section className="py-8 border-t border-border">
      <h2 className="text-xl font-extrabold">{COPY.productsTitle}</h2>
      <p className="mt-1 text-sm text-text-secondary">{COPY.productsSub}</p>
      {!catalog ? <div className="py-10 flex justify-center"><Spinner /></div> : catalog.products.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">{COPY.noProducts}</p>
      ) : (
        <>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={COPY.search}
              className="w-full sm:w-64 rounded-xl bg-bg-panel border border-border px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-cyan" />
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {[{ _id: '', name: COPY.all }, ...catalog.categories].map(c => (
                <button key={c._id || 'all'} onClick={() => setCat(String(c._id))}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${cat === String(c._id) ? 'bg-cyan text-bg border-cyan' : 'bg-bg-panel text-text-secondary border-border hover:border-cyan'}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          {list.length === 0 ? <p className="mt-6 text-sm text-text-muted">{COPY.noMatch}</p> : (
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {list.map(p => <ProductCard key={p._id} p={p} {...actions} />)}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Photo({ p, className }) {
  return p.photos?.[0]
    ? <img src={p.photos[0]} alt={p.name} loading="lazy" className={`object-cover bg-bg-panel ${className}`} />
    : <div className={`flex items-center justify-center bg-bg-panel text-4xl ${className}`}>🛒</div>;
}

function Sizes({ p }) {
  if (p.priceMode === 'on_request') return <div className="text-sm font-bold text-amber">{COPY.priceOnRequest}</div>;
  return (
    <ul className="space-y-1">
      {p.variants.map(v => (
        <li key={v._id} className="flex justify-between gap-3 text-sm">
          <span className="text-text-secondary">{v.unitName}{v.minOrderQty > 1 && <span className="text-text-muted text-xs"> · {COPY.minOrder(v.minOrderQty)}</span>}</span>
          {v.available
            ? <span className="font-bold text-text-primary">{ugx(v.priceUGX)}</span>
            : <span className="text-text-muted"><span className="line-through">{ugx(v.priceUGX)}</span> <span className="text-xs">{COPY.outOfStock}</span></span>}
        </li>
      ))}
    </ul>
  );
}

function Tags({ p }) {
  const d = p.details || {};
  const tags = [p.categoryName, d.brand, COPY.conditions[d.condition], d.warranty && `Warranty: ${d.warranty}`, d.origin && `From ${d.origin}`, d.perishable && COPY.fresh].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map(t => <span key={t} className="rounded-full bg-bg px-2 py-0.5 text-[11px] text-text-muted border border-border">{t}</span>)}
    </div>
  );
}

function Actions({ p, shopOpen, isAuthenticated, buyHref, wa, size = 'sm' }) {
  const pad = size === 'lg' ? 'px-5 py-3 text-sm' : 'px-3 py-2 text-xs';
  const btn = `inline-flex items-center justify-center rounded-xl font-bold transition-all ${pad}`;
  const onRequest = p.priceMode === 'on_request';
  const waHref = wa(COPY.waProductText(p.name, p.slug ? productLink(p) : ''));
  return (
    <div className="flex flex-wrap gap-2">
      {onRequest ? (
        waHref && <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={() => track('click_whatsapp')} className={`${btn} bg-green text-bg hover:opacity-90`}>{COPY.askPrice}</a>
      ) : shopOpen || !isAuthenticated ? (
        <a href={buyHref} onClick={() => track('click_order')} className={`${btn} bg-cyan text-bg hover:opacity-90`}>{shopOpen ? COPY.order : COPY.orderClosed}</a>
      ) : (
        <span className={`${btn} bg-bg text-text-muted border border-border`}>{COPY.orderSoon}</span>
      )}
      {!onRequest && waHref && (
        <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={() => track('click_whatsapp')} className={`${btn} bg-bg-panel text-green border border-border hover:border-green`}>{COPY.askAbout}</a>
      )}
      {p.slug && <button onClick={() => shareProduct(p)} className={`${btn} bg-bg-panel text-text-secondary border border-border hover:border-cyan`}>↗ {COPY.share}</button>}
    </div>
  );
}

function ProductCard({ p, ...actions }) {
  const href = p.slug ? `/dwisend/p/${p.slug}` : undefined;
  return (
    <article className="rounded-2xl bg-bg-panel border border-border overflow-hidden flex flex-col">
      <a href={href}><Photo p={p} className="w-full h-44" /></a>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <a href={href} className="font-extrabold hover:text-cyan">{p.name}</a>
          <div className="text-[11px] text-cyan font-bold mt-0.5">{COPY.soldBy}</div>
        </div>
        <Tags p={p} />
        {p.description && <p className="text-sm text-text-secondary line-clamp-2">{p.description}</p>}
        <div className="mt-auto space-y-3"><Sizes p={p} /><Actions p={p} {...actions} /></div>
      </div>
    </article>
  );
}

function ProductDetail({ p, ...actions }) {
  const [shown, setShown] = useState(0);
  const photos = p.photos || [];
  return (
    <section className="pb-8">
      <a href="/dwisend" className="text-xs font-semibold text-text-muted hover:text-cyan">{COPY.backToAll}</a>
      <div className="mt-4 grid sm:grid-cols-2 gap-6">
        <div>
          {photos.length ? <img src={photos[shown]} alt={p.name} className="w-full aspect-square object-cover rounded-2xl border border-border bg-bg-panel" />
            : <Photo p={p} className="w-full aspect-square rounded-2xl border border-border" />}
          {photos.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto">
              {photos.map((src, i) => (
                <button key={src} onClick={() => setShown(i)} className={`shrink-0 rounded-lg border-2 ${i === shown ? 'border-cyan' : 'border-transparent'}`}>
                  <img src={src} alt="" className="w-14 h-14 object-cover rounded-md" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight">{p.name}</h1>
            <div className="text-xs text-cyan font-bold mt-1">{COPY.soldBy}</div>
          </div>
          <Tags p={p} />
          {p.description && <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">{p.description}</p>}
          <div className="rounded-xl bg-bg-panel border border-border p-4"><Sizes p={p} /></div>
          <Actions p={p} {...actions} size="lg" />
        </div>
      </div>
    </section>
  );
}
